export function nextTick (fn) {
  setTimeout(fn, 0)
}

/**
 * Behaves the same as setTimeout except uses requestAnimationFrame() where possible for better performance
 * @param {function} fn The callback function
 * @param {int} delay The delay in milliseconds
 */
export function requestTimeout (fn, delay, userHandle) {
  if (!window.requestAnimationFrame) {
    return window.setTimeout(fn, delay)
  }

  var handle = userHandle || _makeHandle()
  var start = performance ? performance.now() : Date.now()
  var delta

  function loop (time) {
    time = time || Date.now()
    delta = time - start

    handle.remaining = Math.max(delay - delta, 0)

    delta >= delay
      ? fn.call()
      : handle.value = requestAnimationFrame(loop)
  }

  handle.value = requestAnimationFrame(loop)
  handle.running = true
  return handle
}

/**
 * Behaves the same as clearTimeout except uses cancelAnimationFrame() where possible for better performance
 * @param {int|object} fn The callback function
 */
export function clearRequestTimeout (handle) {
  cancelAnimationFrame ? cancelAnimationFrame(handle.value) : clearTimeout(handle)
  handle.running = false
}

/**
 * Behaves the same as setInterval except uses requestAnimationFrame() where possible for better performance
 * @param {function} fn The callback function
 * @param {int} delay The delay in milliseconds
 */
export function requestInterval (fn, delay, userHandle) {
  if (!window.requestAnimationFrame) {
    return window.setInterval(fn, delay)
  }

  var handle = userHandle || _makeHandle()
  var start = performance ? performance.now() : Date.now()
  var delta

  function loop (time) {
    time = time || Date.now()
    delta = time - start

    handle.remaining = Math.max(delay - delta, 0)

    if (delta >= delay) {
      fn.call()
      start = performance ? performance.now() : Date.now()
    }

    handle.value = requestAnimationFrame(loop)
  };

  handle.value = requestAnimationFrame(loop)
  handle.running = true
  return handle
}

/**
 * Behaves the same as clearInterval except uses cancelAnimationFrame() where possible for better performance
 * @param {int|object} fn The callback function
 */
export function clearRequestInterval (handle) {
  cancelAnimationFrame ? cancelAnimationFrame(handle.value) : clearInterval(handle)
  handle.running = false
}

/**
 * Calls a function after each delay interval
 */
export function poll (fn, delay = 2, immediate) {
  var handle = _makeHandle()

  const start = () => {
    if(handle) clearRequestInterval(handle)
    handle = requestInterval(fn, delay * 1000, handle)
  }

  const stop = () => {
    if(handle) clearRequestInterval(handle)
  }

  if(immediate) {
    fn()
    start()
  }

  return { start, stop, handle }
}

/**
 * Similar to poll, but waits for called function's promise (`fn` must return a promise) to resolve before starting the next interval
 */
export function pollPromise (fn, delay = 2, immediate) {
  var handle = _makeHandle()

  const wrapper = () => {
    fn()
      .finally(() => handle.running == true ? start() : null)
  }

  const start = () => {
    if(handle) clearRequestTimeout(handle)
    handle = requestTimeout(wrapper, delay * 1000, handle)
  }

  const stop = () => {
    if(handle) clearRequestTimeout(handle)
  }

  if(immediate) {
    handle.running = true
    wrapper()
  }

  return { start, stop, handle }
}

export function countdown (target, tickFn, completeFn) {
  const cb = () => {
    let delta = target - Date.now()
    if(delta > 0)
      tickFn(delta)
    else
      completeFn && completeFn()
  }

  const {start, stop} = poll(cb, 1, true)

  return {start, stop}
}


function _makeHandle() {
  return { value: null, remaining: 0, running: false }
}